import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Layout } from "../components/Navbar";
import { api, BACKEND_URL, getUser, canClinical, clinicalLabel } from "../api";
import { formatProName } from "../lib/proName";
import { toast } from "sonner";
import { Users, Columns2, User, Maximize, Minimize, Maximize2, Minimize2, MessageCircle, Mic, MicOff, Video, VideoOff, LayoutGrid, PhoneOff } from "lucide-react";
import { VideoChatPanel } from "../components/VideoChatPanel";
import { BioPanel } from "../components/BioPanel";

const ERR_KEYS = {
  room_not_open_yet: "video.err_room_not_open_yet",
  room_closed: "video.err_room_closed",
  not_found: "video.err_not_found",
  forbidden: "video.err_not_found",
  not_active: "video.err_not_active",
  auth_required: "video.err_auth_required",
};

function mediaErrorKey(e) {
  switch (e?.name) {
    case "NotAllowedError": case "PermissionDeniedError": return "video.media_denied";
    case "NotFoundError": case "DevicesNotFoundError": return "video.media_notfound";
    case "NotReadableError": case "TrackStartError": return "video.media_busy";
    case "OverconstrainedError": return "video.media_constraints";
    case "SecurityError": return "video.media_insecure";
    case "NotSupportedError": return "video.media_unsupported";
    default: return "video.media_generic";
  }
}

export default function VideoRoom() {
  const { bid } = useParams();
  const { t, i18n } = useTranslation();
  const u = getUser();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const resume = sp.get("resume") === "1";
  const [selfConnected, setSelfConnected] = useState(false);
  const [info, setInfo] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [inCall, setInCall] = useState(false);
  const [joining, setJoining] = useState(false);
  const [remoteReady, setRemoteReady] = useState(false);
  const [timer, setTimer] = useState(30 * 60);
  const [view, setView] = useState("remote");   // 'remote' | 'side' | 'self' — solo layout locale, non tocca WebRTC
  const [isFs, setIsFs] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [chatUnread, setChatUnread] = useState(0);
  const [videoExpanded, setVideoExpanded] = useState(false);
  const [micOn, setMicOn] = useState(true);
  const [camOn, setCamOn] = useState(true);
  const [mobilePanelOpen, setMobilePanelOpen] = useState(false);
  const [proTab, setProTab] = useState(() => (typeof localStorage !== "undefined" && localStorage.getItem("yp_video_pro_tab")) || "chat");
  const [viewMenuOpen, setViewMenuOpen] = useState(false);
  const [endConfirm, setEndConfirm] = useState(false);
  const [exitGuard, setExitGuard] = useState(null);
  const isPro = u?.role === "professional";
  const selectProTab = (tab) => { setProTab(tab); try { localStorage.setItem("yp_video_pro_tab", tab); } catch {} };
  const [isDesktop, setIsDesktop] = useState(typeof window !== "undefined" && window.matchMedia("(min-width:1024px)").matches);
  const chatBtnRef = useRef(null);
  const localRef = useRef(null);
  const remoteRef = useRef(null);
  const pcRef = useRef(null);
  const wsRef = useRef(null);
  const streamRef = useRef(null);
  const makingOfferRef = useRef(false);
  const iceQueueRef = useRef([]);
  const stageRef = useRef(null);
  const fsWrapRef = useRef(null);
  const intentionalRef = useRef(false);
  const exitingRef = useRef(false);
  const sentinelRef = useRef(false);
  const stayBtnRef = useRef(null);

  const loadToken = () => {
    setLoadError(null); setInfo(null);
    api.post(`/video/${bid}/token`).then(({data}) => {
      setInfo(data);
      api.get(`/video/${bid}/presence`).then(({data:p}) => setSelfConnected(!!p.self_connected)).catch(()=>{});
    }).catch(err => { const d = err.response?.data?.detail; setLoadError(ERR_KEYS[d] ? t(ERR_KEYS[d]) : t("video.err_generic")); });
  };
  useEffect(() => { window.__ypIntentionalUnload = false; loadToken(); }, [bid]);

  // Assegna lo stream locale al <video> appena viene montato (i video esistono solo in call)
  useEffect(() => {
    if (inCall && localRef.current && streamRef.current) {
      localRef.current.srcObject = streamRef.current;
    }
  }, [inCall]);

  // Chiusura pulita: track, peer connection e WebSocket quando si esce dalla pagina
  const cleanupMedia = () => {
    streamRef.current?.getTracks().forEach(t => t.stop());
    streamRef.current = null;
    pcRef.current?.close(); pcRef.current = null;
    wsRef.current?.close(); wsRef.current = null;
  };
  useEffect(() => cleanupMedia, []);

  // Schermo intero: solo presentazione locale, nessun impatto su media/segnalazione.
  useEffect(() => {
    const on = () => setIsFs(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", on);
    return () => document.removeEventListener("fullscreenchange", on);
  }, []);
  const toggleFullscreen = () => {
    const el = fsWrapRef.current; if (!el) return;
    try {
      if (!document.fullscreenElement) el.requestFullscreen?.();
      else document.exitFullscreen?.();
    } catch {}
  };
  const closeChat = () => { setChatOpen(false); setTimeout(() => chatBtnRef.current?.focus(), 0); };
  useEffect(() => {
    const mq = window.matchMedia("(min-width:1024px)");
    const on = () => setIsDesktop(mq.matches); on();
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);

  useEffect(() => {
    if (!inCall) return;
    const iv = setInterval(() => setTimer(t => { if (t <= 1) { autoComplete(); return 0; } return t - 1; }), 1000);
    return () => clearInterval(iv);
  }, [inCall]);

  const autoComplete = async () => { try { await api.post(`/video/${bid}/tick`); } catch{} };

  const wsSend = (obj) => { const ws = wsRef.current; if (ws && ws.readyState === 1) ws.send(JSON.stringify(obj)); };

  // Initiator deterministico: SOLO il professionista crea/rigenera l'offerta, sempre sulla pc corrente.
  const makeOffer = async () => {
    if (u.role !== "professional") return;
    const pc = pcRef.current;
    if (!pc || makingOfferRef.current || pc.signalingState !== "stable") return;
    try {
      makingOfferRef.current = true;
      const o = await pc.createOffer();
      await pc.setLocalDescription(o);
      wsSend({ type:"offer", sdp: o });
    } catch (e) { console.warn("video: offer failed", e?.name || "err"); }
    finally { makingOfferRef.current = false; }
  };
  const drainIce = async () => {
    const pc = pcRef.current; if (!pc) return;
    const q = iceQueueRef.current; iceQueueRef.current = [];
    for (const c of q) { try { await pc.addIceCandidate(c); } catch (e) { console.warn("video: ice(queued) failed", e?.name || "err"); } }
  };
  // Crea/ricrea una RTCPeerConnection pulita: al rientro del peer non si riusano pc, coda ICE o flag obsoleti.
  const setupPeer = () => {
    try { pcRef.current?.close(); } catch {}
    iceQueueRef.current = [];
    makingOfferRef.current = false;
    const pc = new RTCPeerConnection({ iceServers:[{ urls:"stun:stun.l.google.com:19302" }] });
    pcRef.current = pc;
    const stream = streamRef.current;
    if (stream) stream.getTracks().forEach(t => pc.addTrack(t, stream));
    pc.ontrack = (e) => { if (remoteRef.current) remoteRef.current.srcObject = e.streams[0]; setRemoteReady(true); };
    pc.onicecandidate = (e) => { if (e.candidate) wsSend({ type:"ice", candidate: e.candidate }); };
    return pc;
  };

  const join = async () => {
    if (joining) return;
    setJoining(true);
    if (!navigator.mediaDevices?.getUserMedia) {
      toast.error(t("video.media_unsupported"));
      setJoining(false); return;
    }
    let stream = null;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
    } catch (e) {
      toast.error(t(mediaErrorKey(e)));
      setJoining(false); return;
    }
    streamRef.current = stream;
    try {
      setupPeer();
      const wsUrl = `${BACKEND_URL.replace("https","wss").replace("http","ws")}/api/video/ws/${bid}`;
      const ws = new WebSocket(wsUrl, ["access_token", info.token]); wsRef.current = ws;
      ws.onmessage = async (ev) => {
        let m; try { m = JSON.parse(ev.data); } catch { return; }
        try {
          // peer-present: sono io il nuovo entrato (pc gia' fresca da join). peer-joined: resto io, il peer rientra -> ricreo pc pulita.
          if (m.type === "peer-present") { await makeOffer(); }
          else if (m.type === "peer-joined") { setupPeer(); await makeOffer(); }
          else if (m.type === "peer-left") { setRemoteReady(false); }
          else if (m.type === "replaced") { intentionalRef.current = true; toast.error(t("video.replaced")); cleanupMedia(); setInCall(false); nav("/bookings"); }
          else if (m.type === "offer") {
            const pc = pcRef.current; if (!pc) return;
            await pc.setRemoteDescription(m.sdp);
            await drainIce();
            const a = await pc.createAnswer();
            await pc.setLocalDescription(a);
            wsSend({ type:"answer", sdp: a });
          }
          else if (m.type === "answer") {
            const pc = pcRef.current; if (!pc) return;
            if (pc.signalingState === "have-local-offer") { await pc.setRemoteDescription(m.sdp); await drainIce(); }
          }
          else if (m.type === "ice") {
            const pc = pcRef.current; if (!pc) return;
            if (pc.remoteDescription && pc.remoteDescription.type) { try { await pc.addIceCandidate(m.candidate); } catch (e) { console.warn("video: ice failed", e?.name || "err"); } }
            else { iceQueueRef.current.push(m.candidate); }  // accoda finche' manca la descrizione remota
          }
        } catch (e) { console.warn("video: signaling error", e?.name || "err"); }
      };
      // Nessuna offerta all'apertura: si attende la conferma di presenza del peer dal server.
      setInCall(true);
    } catch (e) {
      stream.getTracks().forEach(t => t.stop());
      streamRef.current = null;
      pcRef.current?.close(); pcRef.current = null;
      wsRef.current?.close(); wsRef.current = null;
      toast.error(t("video.conn_err"));
    } finally { setJoining(false); }
  };

  const doExit = async (dest = "/bookings") => {
    if (exitingRef.current) return;               // esattamente una volta (anti doppio-tap / navigazioni ripetute)
    exitingRef.current = true;
    intentionalRef.current = true;                // uscita intenzionale: nessun blocco navigazione
    setEndConfirm(false); setExitGuard(null);
    try { if (u.role === "professional") await api.post(`/video/${bid}/complete`); } catch {}
    cleanupMedia();
    setInCall(false);
    nav(dest);
  };
  const guardStay = () => setExitGuard(null);
  const guardLeave = () => doExit(exitGuard?.to || "/bookings");
  const onGuardKey = (e) => {
    if (e.key === "Escape") { e.preventDefault(); guardStay(); return; }
    if (e.key === "Tab") {
      const btns = e.currentTarget.querySelectorAll("button");
      if (!btns.length) return;
      const first = btns[0], last = btns[btns.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  };
  useEffect(() => { if (exitGuard) { const id = setTimeout(() => stayBtnRef.current?.focus(), 30); return () => clearTimeout(id); } }, [exitGuard]);
  // Batch E: protezione uscite involontarie SOLO mentre la stanza è attiva. BrowserRouter (non data router)
  // -> niente useBlocker: uso beforeunload + sentinella history/popstate + intercetto click sui link interni.
  useEffect(() => {
    if (!inCall) return;
    const onBU = (e) => { if (intentionalRef.current || window.__ypIntentionalUnload) return; e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", onBU);
    return () => window.removeEventListener("beforeunload", onBU);
  }, [inCall]);
  useEffect(() => {
    if (!inCall) return;
    if (!sentinelRef.current) { window.history.pushState({ ypRoom: true }, ""); sentinelRef.current = true; }
    const onPop = () => { if (intentionalRef.current) return; window.history.pushState({ ypRoom: true }, ""); setExitGuard({ type: "back" }); };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [inCall]);
  useEffect(() => {
    if (!inCall) return;
    const onClickCapture = (e) => {
      if (intentionalRef.current) return;
      const a = e.target.closest && e.target.closest("a[href]");
      if (!a || a.target === "_blank" || a.hasAttribute("download")) return;
      let url; try { url = new URL(a.href, window.location.origin); } catch { return; }
      if (url.origin !== window.location.origin) return;       // esterno -> lo gestisce beforeunload
      if (url.pathname.startsWith("/video/")) return;           // stessa stanza -> ignora
      e.preventDefault(); e.stopPropagation();
      setExitGuard({ type: "link", to: url.pathname + url.search + url.hash });
    };
    document.addEventListener("click", onClickCapture, true);
    return () => document.removeEventListener("click", onClickCapture, true);
  }, [inCall]);
  // Batch E1: guard centralizzato per navigazioni PROGRAMMATICHE (React Router navigate / history push|replace).
  // Attivo solo in-call, ripristina i metodi originali all'uscita: nessuna patch globale permanente.
  useEffect(() => {
    if (!inCall) return;
    const origPush = window.history.pushState.bind(window.history);
    const origReplace = window.history.replaceState.bind(window.history);
    const guard = (orig) => (state, title, url) => {
      if (intentionalRef.current || url == null) return orig(state, title, url);
      try {
        const u2 = new URL(url, window.location.origin);
        if (u2.origin === window.location.origin && !u2.pathname.startsWith("/video/")) {
          setExitGuard({ type: "programmatic", to: u2.pathname + u2.search + u2.hash });
          return;   // blocca la navigazione programmatica non critica; URL invariato
        }
      } catch {}
      return orig(state, title, url);
    };
    window.history.pushState = guard(origPush);
    window.history.replaceState = guard(origReplace);
    return () => { window.history.pushState = origPush; window.history.replaceState = origReplace; };
  }, [inCall]);

  const toggleTrack = (kind) => {
    const s = streamRef.current; if (!s) return;
    const next = !(kind === "audio" ? micOn : camOn);
    const tracks = kind === "audio" ? s.getAudioTracks() : s.getVideoTracks();
    tracks.forEach(t => t.enabled = next);
    if (kind === "audio") setMicOn(next); else setCamOn(next);
  };

  const fmt = (s) => `${String(Math.floor(s/60)).padStart(2,"0")}:${String(s%60).padStart(2,"0")}`;
  // Il paziente vede il professionista con appellativo per lingua attiva; il pro vede il nickname del paziente
  const peerLabel = info?.peer_name ? (info.peer_title ? formatProName({ title: info.peer_title, name: info.peer_name }, i18n.language) : info.peer_name) : "";
  // Layout inquadrature (solo CSS, i nodi <video> restano montati: nessun re-attach dello stream, nessuna rinegoziazione)
  const BIG = "absolute inset-0 w-full h-full object-contain bg-slate-900";
  const PIP = "absolute bottom-3 right-3 w-1/3 max-w-[168px] aspect-video object-contain rounded-lg ring-2 ring-white/80 shadow-lg bg-slate-900 z-20";
  const SIDE_L = "absolute inset-x-0 top-0 h-1/2 w-full object-contain bg-slate-900 md:inset-y-0 md:left-0 md:top-0 md:h-full md:w-1/2";
  const SIDE_R = "absolute inset-x-0 bottom-0 h-1/2 w-full object-contain bg-slate-900 md:inset-y-0 md:right-0 md:left-auto md:top-0 md:h-full md:w-1/2";
  const localBox = view === "self" ? BIG : view === "side" ? SIDE_L : PIP;
  const remoteBox = view === "self" ? PIP : view === "side" ? SIDE_R : BIG;
  const VIEW_MODES = [
    { key: "remote", label: "video.view_remote", Icon: Users },
    { key: "side", label: "video.view_side", Icon: Columns2 },
    { key: "self", label: "video.view_self", Icon: User },
  ];
  const fsSupported = typeof document !== "undefined" && !!document.fullscreenEnabled;
  return (<Layout><div className="yp-container py-8">
    <h1 className="text-2xl font-bold mb-1">{t("video.title")}</h1>
    {peerLabel && <p className="text-slate-600 mb-4" data-testid="video-peer-name">{t("video.peer_with")} {peerLabel}</p>}
    {!info && !loadError && <div data-testid="video-loading">{t("video.loading")}</div>}
    {loadError && (
      <div className="yp-card p-6 text-center" data-testid="video-error">
        <p className="text-red-600 font-medium">{loadError}</p>
        <p className="text-sm text-slate-500 mt-1">{t("video.error_subtitle")}</p>
        <div className="mt-4 flex gap-2 justify-center">
          <button className="yp-btn yp-btn-outline" onClick={loadToken} data-testid="video-retry-btn">{t("video.retry")}</button>
          <button className="yp-btn yp-btn-ghost" onClick={()=>nav("/bookings")} data-testid="video-back-btn">{t("video.back")}</button>
        </div>
      </div>)}
    {info && !inCall && (
      <div className="yp-card p-6" data-testid="video-waiting-room">
        {selfConnected ? (<>
          <p className="font-medium" data-testid="video-takeover-title">{t("video.takeover_title")}</p>
          <div className="mt-4 flex gap-2 flex-wrap">
            <button className="yp-btn yp-btn-ghost" onClick={()=>nav("/bookings")} data-testid="video-takeover-stay">{t("video.stay_other")}</button>
            <button className="yp-btn yp-btn-primary" onClick={()=>{ setSelfConnected(false); join(); }} data-testid="video-takeover-continue">{t("video.continue_here")}</button>
          </div>
        </>) : (<>
          <p className="font-medium">{t("video.waiting_title")}</p>
          <p className="text-sm text-slate-500 mt-1">{t("video.waiting_desc")}</p>
          <button className="yp-btn yp-btn-primary mt-4" onClick={join} disabled={joining} data-testid="video-join-btn">{joining ? t("video.joining") : (resume ? t("video.resume") : t("video.join"))}</button>
          <button className="yp-btn yp-btn-ghost mt-2 ml-2" onClick={()=>nav("/bookings")} data-testid="video-waiting-back-btn">{t("video.back")}</button>
        </>)}
      </div>)}
    {inCall && (
      <div className="yp-card p-4 yp-vroom-card">
        {/* Barra superiore: solo desktop; su mobile i comandi sono nella barra contestuale in basso */}
        <div className="items-center justify-between gap-2 mb-2 flex-wrap hidden lg:flex">
          <div className="font-mono text-lg" data-testid="video-timer">⏱ {fmt(timer)}</div>
          <div className="flex items-center gap-2">
            <div role="group" aria-label={t("video.view_label")} className="inline-flex rounded-lg border border-slate-200 overflow-hidden" data-testid="video-view-switch">
              {VIEW_MODES.map(m => (
                <button key={m.key} type="button" onClick={()=>setView(m.key)}
                  aria-pressed={view===m.key} aria-label={t(m.label)} title={t(m.label)}
                  data-testid={`video-view-${m.key}`}
                  className={`px-2.5 py-1.5 text-sm flex items-center gap-1.5 min-h-[40px] transition-colors ${view===m.key ? "bg-blue-600 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>
                  <m.Icon size={16} aria-hidden="true" />
                  <span className="hidden sm:inline">{t(m.label)}</span>
                </button>
              ))}
            </div>
            {fsSupported && (
              <button type="button" onClick={toggleFullscreen}
                aria-label={t(isFs ? "video.fullscreen_exit" : "video.fullscreen")} title={t(isFs ? "video.fullscreen_exit" : "video.fullscreen")}
                data-testid="video-fullscreen-btn" className="yp-btn yp-btn-outline !px-2.5 !py-1.5 min-h-[40px]">
                {isFs ? <Minimize size={16} aria-hidden="true" /> : <Maximize size={16} aria-hidden="true" />}
              </button>
            )}
            <button type="button" onClick={()=>setVideoExpanded(v=>!v)} aria-pressed={videoExpanded}
              aria-label={t(videoExpanded ? "video.video_reduce" : "video.video_expand")} title={t(videoExpanded ? "video.video_reduce" : "video.video_expand")}
              data-testid="video-expand-btn" className="yp-btn yp-btn-outline !px-2.5 !py-1.5 min-h-[40px]">
              {videoExpanded ? <Minimize2 size={16} aria-hidden="true" /> : <Maximize2 size={16} aria-hidden="true" />}
            </button>
          </div>
        </div>
        {/* Regione media+pannelli: mobile a tutta area (fixed), desktop nel flusso Batch C. I <video> restano montati. */}
        <div ref={fsWrapRef} data-testid="video-media-wrap" className="yp-vroom-mobile flex flex-col min-h-0 lg:gap-3 lg:h-[68vh]">
          <div ref={stageRef} data-testid="video-stage"
            className={`relative w-full bg-slate-900 overflow-hidden lg:aspect-auto ${mobilePanelOpen ? "basis-1/2 min-h-0" : "flex-1 min-h-0"} lg:basis-auto ${(!videoExpanded && isDesktop) ? "lg:h-1/2" : "lg:flex-1"}`}>
            <video ref={localRef} autoPlay muted playsInline className={`${localBox} [transform:scaleX(-1)]`} data-testid="video-local" />
            <video ref={remoteRef} autoPlay playsInline className={remoteBox} data-testid="video-remote" />
            {!remoteReady && <div className={`${remoteBox} flex items-center justify-center text-slate-400 text-sm z-10`} data-testid="video-remote-waiting">{t("video.remote_waiting")}</div>}
            <div className="absolute top-2 left-2 z-20 font-mono text-sm bg-black/50 text-white px-2 py-1 rounded lg:hidden" data-testid="video-timer-mobile">⏱ {fmt(timer)}</div>
          </div>
          {/* Pannelli desktop (Batch C) */}
          {!videoExpanded && isDesktop && (
            <div className="hidden lg:flex lg:gap-3 lg:h-1/2 lg:min-h-0" data-testid="video-desktop-panels">
              <div className="flex-1 min-w-0 h-full"><VideoChatPanel peerId={info?.peer_id} open embedded onClose={()=>{}} onUnread={setChatUnread} /></div>
              <div className="flex-1 min-w-0 h-full flex flex-col">
                {isPro && info?.peer_id && canClinical(getUser()) && <a href={`/clinica/${info.peer_id}`} target="_blank" rel="noreferrer" className="yp-btn yp-btn-outline !py-1 !px-2 text-xs mb-1 shrink-0 self-start" data-testid="video-clinical-btn">{clinicalLabel(getUser())}</a>}
                {isPro && info?.peer_id && canClinical(getUser()) && <a href={`/clinica/${info.peer_id}?ep=videoconsulto&vc=${bid}`} target="_blank" rel="noreferrer" className="yp-btn yp-btn-primary !py-1 !px-2 text-xs mb-1 shrink-0 self-start" data-testid="video-redact-btn">Redigi documento clinico</a>}
                <BioPanel peerId={info?.peer_id} isPro={isPro} />
              </div>
            </div>)}
          {/* Pannello mobile: paziente = chat; professionista = schede Chat/Bio sanitaria (una alla volta) */}
          {!isDesktop && (
            <div className={`flex flex-col min-h-0 bg-white ${mobilePanelOpen ? "basis-1/2" : "hidden"}`} data-testid="video-mobile-panel">
              {isPro && (
                <div role="tablist" aria-label={t("video.area")} className="flex shrink-0 border-b border-slate-200" data-testid="video-mobile-tabs">
                  <button role="tab" type="button" aria-selected={proTab==="chat"} onClick={()=>selectProTab("chat")}
                    data-testid="video-tab-chat"
                    className={`flex-1 min-h-[44px] text-sm font-medium ${proTab==="chat" ? "text-blue-600 border-b-2 border-blue-600" : "text-slate-500"}`}>{t("video.chat")}</button>
                  <button role="tab" type="button" aria-selected={proTab==="bio"} onClick={()=>selectProTab("bio")}
                    data-testid="video-tab-bio"
                    className={`flex-1 min-h-[44px] text-sm font-medium ${proTab==="bio" ? "text-blue-600 border-b-2 border-blue-600" : "text-slate-500"}`}>{t("video.bio_title")}</button>
                </div>)}
              <div className={`flex-1 min-h-0 ${(!isPro || proTab==="chat") ? "" : "hidden"}`}>
                <VideoChatPanel peerId={info?.peer_id} embedded open={mobilePanelOpen && (!isPro || proTab==="chat")} onClose={()=>setMobilePanelOpen(false)} onUnread={setChatUnread} />
              </div>
              {isPro && proTab==="bio" && mobilePanelOpen && (
                <div className="flex-1 min-h-0"><BioPanel peerId={info?.peer_id} isPro visible /></div>)}
            </div>)}
        </div>
        {/* Barra comandi desktop (Batch C) */}
        <div className="mt-4 gap-2 justify-center flex-wrap hidden lg:flex">
          <button ref={chatBtnRef} type="button" className="yp-btn yp-btn-outline relative flex items-center gap-1.5" data-testid="video-chat-btn" aria-label={t("video.chat")} aria-expanded={chatOpen} onClick={()=>{ if (chatOpen) closeChat(); else setChatOpen(true); }}>
            <MessageCircle size={16} aria-hidden="true" /> {t("video.chat")}
            {chatUnread > 0 && <span className="absolute -top-1.5 -right-1.5 bg-red-500 text-white text-[10px] leading-4 min-w-[16px] px-1 rounded-full text-center" role="status" data-testid="video-chat-unread">{chatUnread}</span>}
          </button>
          <button className="yp-btn yp-btn-outline" data-testid="webrtc-toggle-mic-btn" onClick={()=>toggleTrack("audio")} aria-pressed={micOn}>🎤 {t("video.mic")}</button>
          <button className="yp-btn yp-btn-outline" data-testid="webrtc-toggle-cam-btn" onClick={()=>toggleTrack("video")} aria-pressed={camOn}>📷 {t("video.cam")}</button>
          <button className="yp-btn yp-btn-ghost text-red-600" onClick={()=>doExit()} data-testid="webrtc-end-call-btn">{t("video.end")}</button>
        </div>
        {/* Barra comandi contestuale mobile: sostituisce la navbar mobile globale nella stanza */}
        <nav className="yp-vroom-cmd lg:hidden" aria-label={t("video.title")} data-testid="video-cmdbar">
          <button type="button" onClick={()=>toggleTrack("audio")} aria-pressed={micOn} aria-label={t("video.mic")} data-testid="video-mic-btn-m" className="yp-vcmd">
            {micOn ? <Mic size={20} aria-hidden="true" /> : <MicOff size={20} aria-hidden="true" className="text-red-600" />}
            <span>{t("video.mic")}</span>
          </button>
          <button type="button" onClick={()=>toggleTrack("video")} aria-pressed={camOn} aria-label={t("video.cam")} data-testid="video-cam-btn-m" className="yp-vcmd">
            {camOn ? <Video size={20} aria-hidden="true" /> : <VideoOff size={20} aria-hidden="true" className="text-red-600" />}
            <span>{t("video.cam")}</span>
          </button>
          <button type="button" onClick={()=>setMobilePanelOpen(o=>!o)} aria-pressed={mobilePanelOpen} aria-label={isPro ? t("video.area") : t("video.chat")} data-testid="video-panel-btn-m" className="yp-vcmd">
            <span className="relative flex items-center justify-center">
              <MessageCircle size={20} aria-hidden="true" />
              {chatUnread > 0 && <span className="absolute -top-2 -right-2.5 bg-red-500 text-white text-[10px] leading-4 min-w-[16px] px-1 rounded-full text-center" role="status" data-testid="video-panel-unread-m">{chatUnread}</span>}
            </span>
            <span>{isPro ? t("video.area") : t("video.chat")}</span>
          </button>
          <div className="relative flex-1 flex">
            <button type="button" onClick={()=>setViewMenuOpen(o=>!o)} aria-haspopup="menu" aria-expanded={viewMenuOpen} aria-label={t("video.view_label")} data-testid="video-view-btn-m" className="yp-vcmd w-full">
              <LayoutGrid size={20} aria-hidden="true" />
              <span>{t("video.view_label")}</span>
            </button>
            {viewMenuOpen && (<>
              <div className="fixed inset-0 z-40" onClick={()=>setViewMenuOpen(false)} data-testid="video-view-menu-backdrop" />
              <div role="menu" aria-label={t("video.view_label")} data-testid="video-view-menu"
                className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 z-50 bg-white border border-slate-200 rounded-xl shadow-xl py-1 min-w-[160px]">
                {VIEW_MODES.map(m => (
                  <button key={m.key} role="menuitemradio" aria-checked={view===m.key} type="button"
                    onClick={()=>{ setView(m.key); setViewMenuOpen(false); }}
                    data-testid={`video-view-${m.key}-m`}
                    className={`flex items-center gap-2 w-full px-3 min-h-[44px] text-sm text-left ${view===m.key ? "text-blue-600 font-semibold" : "text-slate-700"}`}>
                    <m.Icon size={16} aria-hidden="true" /> {t(m.label)}
                  </button>))}
              </div>
            </>)}
          </div>
          <button type="button" onClick={()=>setMobilePanelOpen(false)} aria-pressed={!mobilePanelOpen} aria-label={t("video.video_expand")} data-testid="video-expand-btn-m" className="yp-vcmd">
            <Maximize2 size={20} aria-hidden="true" />
            <span>{t("video.video_expand")}</span>
          </button>
          <button type="button" onClick={()=>setEndConfirm(true)} aria-label={t("video.end")} data-testid="video-end-btn-m" className="yp-vcmd yp-vcmd-danger">
            <PhoneOff size={20} aria-hidden="true" />
            <span>{t("video.end")}</span>
          </button>
        </nav>
        {/* Dialogo conferma Termina */}
        {endConfirm && (
          <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4" data-testid="video-end-dialog-overlay" onClick={()=>setEndConfirm(false)}>
            <div role="dialog" aria-modal="true" aria-label={t("video.end_confirm_title")} data-testid="video-end-dialog"
              className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-5" onClick={e=>e.stopPropagation()}
              style={{ paddingBottom: "calc(1.25rem + env(safe-area-inset-bottom, 0px))" }}>
              <p className="font-semibold text-base mb-4">{t("video.end_confirm_title")}</p>
              <div className="flex flex-col gap-2">
                <button type="button" className="yp-btn yp-btn-outline w-full" onClick={()=>setEndConfirm(false)} data-testid="video-end-stay">{t("video.end_stay")}</button>
                <button type="button" className="yp-btn yp-btn-primary w-full !bg-red-600 hover:!bg-red-700" onClick={()=>doExit("/bookings")} data-testid="video-end-leave">{t("video.end_leave")}</button>
              </div>
            </div>
          </div>)}
        {/* Batch E: dialogo blocco navigazione (link interni, pulsante Indietro del browser) */}
        {exitGuard && (
          <div className="fixed inset-0 z-[70] bg-black/50 flex items-center justify-center p-4" data-testid="video-exit-guard-overlay" onClick={guardStay}>
            <div role="dialog" aria-modal="true" aria-label={t("video.exit_guard_msg")} data-testid="video-exit-guard-dialog"
              onClick={e=>e.stopPropagation()} onKeyDown={onGuardKey}
              className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-5"
              style={{ paddingBottom: "calc(1.25rem + env(safe-area-inset-bottom, 0px))" }}>
              <p className="font-semibold text-base mb-4">{t("video.exit_guard_msg")}</p>
              <div className="flex flex-col gap-2">
                <button ref={stayBtnRef} type="button" className="yp-btn yp-btn-outline w-full" onClick={guardStay} data-testid="video-exit-guard-stay">{t("video.end_stay")}</button>
                <button type="button" className="yp-btn yp-btn-primary w-full !bg-red-600 hover:!bg-red-700" onClick={guardLeave} data-testid="video-exit-guard-leave">{t("video.end_leave")}</button>
              </div>
            </div>
          </div>)}
      </div>)}
  </div></Layout>);
}
